// Lib
import type Handsontable from 'handsontable/base';
import { cloneDeep, isNumber, isString } from 'lodash';

// Utils
import EditingCleanupHotPlugin, { EDITING_CLEANUP_PLUGIN_NAME } from './EditingCleanupHotPlugin';
import { isFormula } from '../../bridge/utils/dataUtils/formula/tableFormulaPredicateUtils';
import { isCellTypeCurrency, isCellTypePercentage } from '../../../../../common/table/utils/tableCellDataPropertyUtils';
import { stringShouldFormatAsType } from '../../../../../common/table/utils/tableInputGeneralUtils';

// Types
import { CellCoordsObj } from '../../../../../common/table/TableTypes';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';

export const EDITING_PLUGIN_NAME = 'editing';
export const EDITING_PLUGIN_PRIORITY = 1;

/**
 * EditingHotPlugin.ts
 *
 *   - This is a custom plugin that has a high priority (called before all other plugins) used for Milanote table editing setup
 *
 *   - Normalise the Handsontable cell values into values that is inferrable by HyperFormula.
 *
 *      Process:
 *      afterSetDataAtCell hook -> EditingHotPlugin -> FormulaPlugin -> EditingCleanupHotPlugin
 *      For diagram, see https://app.milanote.com/1Qjnx50VbE5g69/table-editing-flow
 *
 * TODO-TABLE-REFACTOR: Split plugin and rename to something more specific, alongside EditingCleanupHotPlugin
 */
class EditingHotPlugin extends MilanoteHotPlugin {
    /**
     * This key will be used as a prop key when enabling this plugin in the HotTable component
     */
    static get PLUGIN_KEY(): string {
        return EDITING_PLUGIN_NAME;
    }

    static get PLUGIN_PRIORITY(): number {
        return EDITING_PLUGIN_PRIORITY;
    }

    enablePlugin(): void {
        this.addHook('afterSetDataAtCell', (...args) => this.onAfterSetDataAtCell(...args));
        this.addHook('beforeOnCellMouseDown', (event) => this.onBeforeOnCellMouseDown(event));
        this.addHook('beforeInitWalkontable', (config) => this.beforeInitWalkontable(config));

        // The `super` method sets the `this.enabled` property to `true`.
        // It is a necessary step to update the plugin's settings properly.
        super.enablePlugin();
    }

    /**
     * The Formula plugin listens to the `afterSetDataAtCell` hook to update the formula cells.
     *
     * Since Milanote table has its own implementation of cell types and cell formatting,
     * which is external to Handsontable and Hyperformula. Before we pass in values to Hyperformula,
     * we need to update the values first so that Hyperformula would be able to infer the HF cell type.
     *
     * This `onAfterSetDataAtCell` function:
     *  - Saves the original changes in `EditingCleanupHotPlugin`, for restoration later after all the other
     *    plugins have run
     *  - Convert the changes to a readable format for the formula plugin
     *
     * The changes will be returned to its original format in `EditingCleanupHotPlugin`
     */
    onAfterSetDataAtCell(changes: Handsontable.CellChange[], source?: Handsontable.ChangeSource): void {
        const milanoteEditingCleanupPlugin = this.milanoteHot.getPlugin(
            EDITING_CLEANUP_PLUGIN_NAME,
        ) as EditingCleanupHotPlugin;

        milanoteEditingCleanupPlugin.setOriginalChanges(cloneDeep(changes));

        // These changes will be saved to Hyperformula, and get converted back to the original format in
        // `EditingCleanupHotPlugin`
        changes.forEach(([row, col, prevValue, newValue], index) => {
            if (!isNumber(col)) return;

            const prevHFValue = this.normaliseCellValueForHyperFormula(prevValue, { row, col }, source);
            const newHFValue = this.normaliseCellValueForHyperFormula(newValue, { row, col }, source);

            changes[index] = [row, col, prevHFValue, newHFValue];
        });
    }

    onBeforeOnCellMouseDown(event: MouseEvent): void {
        if (this.milanoteHot.milanoteProps.isReadOnly) event.stopImmediatePropagation();
    }

    /**
     * Disable the default onContainerElementResize, as it will cause the table to rerender on Handsontable's side.
     * This is not applicable to Milanote's tables, as the container element's size will have limited effect on the
     * content of the table.
     */
    beforeInitWalkontable(config: any): void {
        config.onContainerElementResize = null;
    }

    /********************
     * CELL TEXT FORMATTING
     ********************/

    /**
     * Normalise the Handsontable cell value into the plain value that HyperFormula expects.
     */
    normaliseCellValueForHyperFormula = (
        cellValue: string | number | null,
        cellCoords: CellCoordsObj,
        source?: string,
    ): string | number | null => {
        const { locale } = this.milanoteHot.milanoteProps || {};
        const { cellData } = this.milanoteHot.getCellMeta(cellCoords.row, cellCoords.col) || {};

        let newHFValue = cellValue;

        // Formatting numbers here so that english number formatting will be saved to Hyperformula
        if (isString(cellValue)) {
            newHFValue = stringShouldFormatAsType(cellValue, true, locale)?.newCellValue || cellValue;
        }

        if (newHFValue !== null && !isFormula(cellData?.value)) {
            // If currency, prefix a $ to the value given to Hyperformula, so that it can read it as a currency
            if (isCellTypeCurrency(cellData)) {
                newHFValue = `$${newHFValue}`;
            }

            // If percentage, convert to `x%` format so that Hyperformula can read it as a percentage
            if (isCellTypePercentage(cellData) && isNumber(newHFValue)) {
                newHFValue = `${newHFValue * 100}%`;
            }

            if (isCellTypePercentage(cellData) && isString(newHFValue) && !newHFValue.endsWith('%')) {
                newHFValue = `${parseInt(newHFValue) * 100}%`;
            }
        }

        if (newHFValue === '') return null;

        return newHFValue;
    };
}

export default EditingHotPlugin;
